import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Session } from "@supabase/supabase-js";
import { checkUserRole } from "@/lib/userUtils";
import { OAuthButton } from "@/components/auth/OAuthButton";
import { getAvailableProviders } from "@/lib/oauthProviders";
import { Separator } from "@/components/ui/separator";
import { Eye, EyeOff, AlertCircle, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DollarBillIcon } from "@/components/DollarBillIcon";

/** Canonical origin (no www) so password reset redirect matches Supabase Redirect URL allowlist */
function getCanonicalAuthOrigin(): string {
  try {
    const url = new URL(window.location.origin);
    if (url.hostname.startsWith("www.")) {
      url.hostname = url.hostname.slice(4);
    }
    return url.origin;
  } catch {
    return window.location.origin;
  }
}

export const Auth = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const hasRedirectedRef = useRef(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccess, setAuthSuccess] = useState<string | null>(null);

  const checkOnboardingStatus = useCallback(async (userId: string) => {
    // Prevent multiple redirects
    if (hasRedirectedRef.current) return;
    
    try {
      // Check if user is global admin
      const isGlobalAdmin = await checkUserRole('global_admin');
      
      if (isGlobalAdmin) {
        hasRedirectedRef.current = true;
        setIsCheckingAuth(false);
        navigate("/dashboard");
        return;
      }

      // Check if user has completed onboarding
      const { data: profile } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("user_id", userId)
        .single();

      hasRedirectedRef.current = true;
      setIsCheckingAuth(false);
      
      // Check if there's a stored redirect path
      const redirectPath = sessionStorage.getItem("redirect_after_auth");
      if (redirectPath && redirectPath.startsWith("/dashboard")) {
        sessionStorage.removeItem("redirect_after_auth");
        navigate(redirectPath, { replace: true });
        return;
      }
      
      if (profile?.company_id) {
        navigate("/dashboard");
      } else {
        navigate("/onboarding");
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      // If there's an error, show the auth form anyway
      setIsCheckingAuth(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Don't run if we've already redirected
    if (hasRedirectedRef.current) return;

    let isMounted = true;
    let initialCheckDone = false;

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        initialCheckDone = true;
        
        // If user is already logged in, redirect them
        if (session?.user && !hasRedirectedRef.current) {
          await checkOnboardingStatus(session.user.id);
          return;
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        initialCheckDone = true;
      } finally {
        // Only set to false if we haven't redirected and component is still mounted
        if (!hasRedirectedRef.current && isMounted) {
          setIsCheckingAuth(false);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Don't process if we've already redirected, component unmounted, or initial check not done
        if (hasRedirectedRef.current || !isMounted || !initialCheckDone) return;
        
        // Only process SIGNED_IN events (not the initial session check)
        if (event !== 'SIGNED_IN' && event !== 'SIGNED_OUT') return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // If user signs in, check onboarding status and redirect
        if (session?.user && event === 'SIGNED_IN' && !hasRedirectedRef.current) {
          // Check if there's a stored redirect path
          const redirectPath = sessionStorage.getItem("redirect_after_auth");
          if (redirectPath && redirectPath.startsWith("/dashboard")) {
            sessionStorage.removeItem("redirect_after_auth");
            hasRedirectedRef.current = true;
            setIsCheckingAuth(false);
            navigate(redirectPath, { replace: true });
            return;
          }
          await checkOnboardingStatus(session.user.id);
        } else if (!session?.user && event === 'SIGNED_OUT') {
          setIsCheckingAuth(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, checkOnboardingStatus]);

  // Handle password reset link error (Supabase redirects to root with #error=otp_expired when redirect URL isn't allowlisted)
  useEffect(() => {
    const resetError = searchParams.get("reset_error");
    if (resetError === "expired") {
      setAuthError("Password reset link is invalid or has expired. Please request a new one below.");
      setShowForgotPassword(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("reset_error");
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setAuthError(null);
    setAuthSuccess(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const firstName = formData.get("firstName") as string;
    const lastName = formData.get("lastName") as string;

    const redirectUrl = `${window.location.origin}/`;

    const { data: signUpData, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      },
    });

    if (error) {
      let errorMessage = error.message;
      
      // Provide user-friendly error messages
      if (error.message.includes("already registered") || error.message.includes("already exists")) {
        errorMessage = "An account with this email already exists. Please sign in instead.";
      } else if (error.message.includes("password")) {
        errorMessage = "Password must be at least 6 characters long.";
      } else if (error.message.includes("email")) {
        errorMessage = "Please enter a valid email address.";
      }
      
      setAuthError(errorMessage);
      setLoading(false);
      return;
    }

    // Check if email confirmation is required
    if (signUpData.user && !signUpData.session) {
      // Email confirmation required
      setAuthSuccess("Account created successfully! Please check your email and click the verification link to confirm your account. Once verified, you can sign in. If you didn't receive the email, check your spam folder.");
      setLoading(false);
      return;
    }

    // If we have a session, user is already confirmed and signed in
    if (signUpData.session) {
      setAuthSuccess("Account created! Redirecting to onboarding...");
      // Keep loading state active - onAuthStateChange will handle the redirect
      // Don't set loading to false here, let the redirect happen first
      // The loading state will be cleared when component unmounts or redirects
    } else {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setAuthError(null);
    setAuthSuccess(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      let errorMessage = error.message;
      
      // Provide user-friendly error messages
      if (error.message === 'Invalid login credentials' || error.message.includes('Invalid login')) {
        errorMessage = "Invalid email or password. If you signed up with Google, use the \"Continue with Google\" button below instead.";
      } else if (error.message.includes('Email not confirmed') || error.message.includes('email_not_confirmed')) {
        errorMessage = 'Please verify your email address before signing in. Check your inbox for the verification link.';
      } else if (error.message.includes('too many requests')) {
        errorMessage = 'Too many login attempts. Please wait a few minutes before trying again.';
      }
      
      setAuthError(errorMessage);
      setLoading(false);
      return;
    }

    // After successful sign-in, the onAuthStateChange will handle the redirect
    // No need to call checkOnboardingStatus here to avoid duplicate checks
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setAuthError(null);
    setAuthSuccess(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: `${getCanonicalAuthOrigin()}/auth/reset-password`,
      });

      if (error) {
        let errorMessage = error.message;
        if (error.message.includes("email")) {
          errorMessage = "Please enter a valid email address.";
        }
        setAuthError(errorMessage);
        setForgotPasswordLoading(false);
        return;
      }

      setAuthSuccess("Password reset email sent! If an account exists with this email, you'll receive a password reset link. Please check your email and spam folder.");
      
      // Close dialog and reset form after a short delay
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
        setAuthSuccess(null);
      }, 3000);
    } catch (error) {
      console.error("Forgot password error:", error);
      setAuthError(
        error instanceof Error 
          ? error.message 
          : "Failed to send password reset email. Please try again."
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Show loading state while checking if user is already authenticated
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3">
          <DollarBillIcon size={64} />
          <h1 className="text-3xl font-bold">Dollar Bill</h1>
        </div>
        
        <Card className="shadow-elegant">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-bold text-center">Welcome</CardTitle>
            <CardDescription className="text-center">
              Sign in or create an account to get started
            </CardDescription>
          </CardHeader>
          <CardContent>
          {/* Error Alert */}
          {authError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription className="flex items-center justify-between">
                <span>{authError}</span>
                <button
                  onClick={() => setAuthError(null)}
                  className="ml-2 hover:opacity-70"
                >
                  <X className="h-4 w-4" />
                </button>
              </AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {authSuccess && (
            <Alert className="mb-4 border-green-500 bg-green-50 dark:bg-green-950/20">
              <AlertCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800 dark:text-green-200">Success</AlertTitle>
              <AlertDescription className="flex items-center justify-between text-green-700 dark:text-green-300">
                <span>{authSuccess}</span>
                <button
                  onClick={() => setAuthSuccess(null)}
                  className="ml-2 hover:opacity-70"
                >
                  <X className="h-4 w-4" />
                </button>
              </AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="signin" className="w-full" onValueChange={() => {
            // Clear errors when switching tabs
            setAuthError(null);
            setAuthSuccess(null);
          }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <div className="relative">
                  <Input
                    id="signin-password"
                    name="password"
                      type={showSignInPassword ? "text" : "password"}
                    required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignInPassword(!showSignInPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showSignInPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>

              {/* OAuth Providers */}
              {getAvailableProviders().length > 0 && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <p className="text-center text-xs text-muted-foreground mb-2">
                    Signed up with Google? Use the same method to sign in.
                  </p>
                  <div className="space-y-2">
                    {getAvailableProviders().map((provider) => (
                      <OAuthButton key={provider.id} provider={provider} />
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                  <Input
                    id="signup-password"
                    name="password"
                      type={showSignUpPassword ? "text" : "password"}
                    minLength={6}
                    required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showSignUpPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Creating account..." : "Create Account"}
                </Button>
              </form>

              {/* OAuth Providers */}
              {getAvailableProviders().length > 0 && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {getAvailableProviders().map((provider) => (
                      <OAuthButton key={provider.id} provider={provider} />
                    ))}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            {/* Error Alert in Dialog */}
            {authError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription className="flex items-center justify-between">
                  <span>{authError}</span>
                  <button
                    onClick={() => setAuthError(null)}
                    className="ml-2 hover:opacity-70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </AlertDescription>
              </Alert>
            )}

            {/* Success Alert in Dialog */}
            {authSuccess && (
              <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
                <AlertCircle className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800 dark:text-green-200">Success</AlertTitle>
                <AlertDescription className="flex items-center justify-between text-green-700 dark:text-green-300">
                  <span>{authSuccess}</span>
                  <button
                    onClick={() => setAuthSuccess(null)}
                    className="ml-2 hover:opacity-70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => {
                  setForgotPasswordEmail(e.target.value);
                  setAuthError(null);
                  setAuthSuccess(null);
                }}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail("");
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={forgotPasswordLoading}>
                {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};
